export default defineNuxtPlugin((nuxtApp) => {
  const { gtagId, conversionId, gtmId } = useRuntimeConfig().public || {}
  const consent = useCookie('cookie_consent', { path: '/', sameSite: 'lax' })
  if (!gtmId) return

  let analyticsConsentGranted = consent.value === 'accepted'
  let gtmLoaded = false
  let gtmScheduled = false
  let gtmFallbackTimer = null
  let cancelScheduledGtmLoad = () => {}
  const activationEvents = ['pointerdown', 'keydown', 'touchstart', 'scroll']

  window.dataLayer = window.dataLayer || []
  function gtag() { window.dataLayer.push(arguments) }
  window.gtag = window.gtag || gtag

  const consentValues = (granted) => ({
    analytics_storage: granted ? 'granted' : 'denied',
    ad_storage: granted ? 'granted' : 'denied',
    ad_user_data: granted ? 'granted' : 'denied',
    ad_personalization: granted ? 'granted' : 'denied'
  })

  gtag('consent', 'default', consentValues(analyticsConsentGranted))

  const loadGtm = () => {
    gtmScheduled = false
    if (gtmLoaded) return

    gtmLoaded = true
    window.dataLayer.push({
      'gtm.start': Date.now(),
      event: 'gtm.js',
      analytics_measurement_id: gtagId || '',
      google_ads_conversion_id: conversionId || ''
    })

    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}`
    document.head.appendChild(script)
  }

  const scheduleGtmLoad = () => {
    // Do not download a 300+ KiB third-party bundle before the visitor has
    // opted in. This also keeps the initial render and its main thread free
    // for the page itself.
    if (!analyticsConsentGranted || gtmLoaded || gtmScheduled) return
    gtmScheduled = true

    const removeActivationListeners = () => {
      activationEvents.forEach((eventName) => {
        window.removeEventListener(eventName, loadAfterActivation)
      })
    }

    const cancelLoad = () => {
      removeActivationListeners()
      if (gtmFallbackTimer) {
        window.clearTimeout(gtmFallbackTimer)
        gtmFallbackTimer = null
      }
      gtmScheduled = false
    }

    const loadAfterActivation = () => {
      cancelLoad()
      if ('requestIdleCallback' in window) window.requestIdleCallback(loadGtm)
      else window.setTimeout(loadGtm, 0)
    }

    activationEvents.forEach((eventName) => {
      window.addEventListener(eventName, loadAfterActivation, { once: true, passive: true })
    })

    // Returning visitors who have already consented are still measured even
    // if they do not interact, but never during the initial render window.
    gtmFallbackTimer = window.setTimeout(loadAfterActivation, 8000)
    cancelScheduledGtmLoad = cancelLoad
  }

  if (analyticsConsentGranted) scheduleGtmLoad()

  window.addEventListener('cookie-consent-updated', (event) => {
    const granted = event?.detail === 'accepted'
    analyticsConsentGranted = granted
    gtag('consent', 'update', consentValues(granted))
    window.dataLayer.push({ event: 'consent_update', consent_granted: granted })
    if (granted) scheduleGtmLoad()
    else cancelScheduledGtmLoad()
  })

  return {
    provide: {
      gtag: {
        event: (event, params = {}) => useTracking().trackEvent(event, params),
        config: () => undefined
      }
    }
  }
})
